import React, { useEffect, useState, useRef } from 'react';
/**
 * Project Flow - Holographic Portal Grid
 * 2030 futuristic project showcase with magnetic interactions
 */
import { motion, useMotionValue, useTransform } from 'framer-motion';
import { Sparkles, Filter, Zap } from 'lucide-react';
import { useTheme } from '../../../core/contexts/ThemeContext';
import { ProjectCard } from './ProjectCard';
import { Project } from './data/projects';
interface ProjectFlowProps {
  projects: Project[];
  onProjectClick: (project: Project) => void;
  scrollPosition: number;
  onScrollChange: (position: number) => void;
  // [VERWIJDERD]: mousePosition is niet langer nodig als prop
}
export function ProjectFlow({
  projects,
  onProjectClick,
  scrollPosition,
  onScrollChange
}: ProjectFlowProps) {
  const {
    getThemeClasses,
    theme: currentTheme
  } = useTheme();
  const theme = getThemeClasses();
  const [activeFilter, setActiveFilter] = useState<string>('all');
  // [VERWIJDERD] mousePosition state is verwijderd
  const [isMobile, setIsMobile] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const isLightTheme = currentTheme === 'light';
  const isNeonTheme = currentTheme === 'neon';
  const isHighContrast = currentTheme === 'highContrast';

  // Detect mobile
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // [VERWIJDERD] Mouse tracking for parallax effects
  // De hele useEffect voor handleMouseMove is verwijderd.

  const categories = [{
    id: 'all',
    label: 'Alles',
    icon: Sparkles
  }, {
    id: 'video',
    label: 'Video & AI',
    icon: Zap
  }, {
    id: 'apps',
    label: 'Apps',
    icon: Zap
  }, {
    id: 'ux',
    label: 'UX Design',
    icon: Zap
  }, {
    id: 'ai',
    label: 'AI Systems',
    icon: Zap
  }];
  const filteredProjects = activeFilter === 'all' ? projects : projects.filter(p => p.category === activeFilter);
  return <motion.div ref={containerRef} initial={{
    opacity: 0
  }} animate={{
    opacity: 1
  }} exit={{
    opacity: 0
  }} className="h-full flex flex-col relative overflow-hidden" style={{
    background: isLightTheme ? 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)' : isHighContrast ? '#000000' : isNeonTheme ? 'radial-gradient(circle at 50% 50%, #0a0a0a 0%, #000000 100%)' : 'radial-gradient(circle at 50% 50%, #0f172a 0%, #020617 100%)'
  }} data-id="element-453">
      {/* Animated Grid Background */}
      <div className="absolute inset-0 opacity-10" data-id="element-454">
        <motion.div animate={{
        backgroundPosition: ['0% 0%', '100% 100%']
      }} transition={{
        duration: 30,
        repeat: Infinity,
        ease: 'linear'
      }} className="w-full h-full" style={{
        backgroundImage: isHighContrast ? 'none' : `linear-gradient(${isNeonTheme ? '#00ffff' : isLightTheme ? '#94a3b8' : '#334155'} 1px, transparent 1px), 
                 linear-gradient(90deg, ${isNeonTheme ? '#00ffff' : isLightTheme ? '#94a3b8' : '#334155'} 1px, transparent 1px)`,
        backgroundSize: '60px 60px'
      }} data-id="element-455" />
      </div>
      
      {/* Floating Orbs - Parallax Background */}
      {/* [VERWIJDERD] De parallax-animatie op de orbs is verwijderd */}
      {!isHighContrast && !isMobile && <div className="absolute inset-0 overflow-hidden pointer-events-none" data-id="element-456">
          {[...Array(5)].map((_, i) => <motion.div key={i} className="absolute rounded-full blur-3xl" style={{
        width: 200 + i * 50,
        height: 200 + i * 50,
        left: `${20 + i * 15}%`,
        top: `${10 + i * 20}%`,
        background: isNeonTheme ? i % 2 === 0 ? 'radial-gradient(circle, rgba(0,255,255,0.1) 0%, transparent 70%)' : 'radial-gradient(circle, rgba(255,0,255,0.1) 0%, transparent 70%)' : 'radial-gradient(circle, rgba(236,72,153,0.1) 0%, transparent 70%)'
      }}
      // VASTE/RUSTIGE ANIMATIE:
      animate={{
        scale: [1, 1.1, 1],
        x: [0, i % 2 === 0 ? 50 : -50, 0]
      }} transition={{
        duration: 20 + i * 5,
        repeat: Infinity,
        ease: 'easeInOut'
      }} data-id="element-457" />)}
        </div>}
        
      {/* Constellation Lines - Desktop only */}
      {!isHighContrast && !isMobile && filteredProjects.length > 1 && <svg className="absolute inset-0 pointer-events-none" style={{
      zIndex: 1
    }} data-id="element-458">
          {filteredProjects.slice(0, -1).map((_, i) => <motion.line key={i} x1={`${(i % 3 + 1) * 25}%`} y1={`${Math.floor(i / 3) * 33 + 20}%`} x2={`${((i + 1) % 3 + 1) * 25}%`} y2={`${Math.floor((i + 1) / 3) * 33 + 20}%`} stroke={isNeonTheme ? '#00ffff' : '#ec4899'} strokeWidth="1" strokeDasharray="5,5" initial={{
        pathLength: 0,
        opacity: 0
      }} animate={{
        pathLength: 1,
        opacity: 0.2
      }} transition={{
        duration: 2,
        delay: i * 0.2
      }} data-id="element-459" />)}
        </svg>}
        
      {/* Header */}
      <div className="relative z-10 px-4 md:px-8 py-4 md:py-6 space-y-4 md:space-y-6" data-id="element-460">
        <motion.div initial={{
        y: -20,
        opacity: 0
      }} animate={{
        y: 0,
        opacity: 1
      }} className="text-center space-y-2 md:space-y-3" style={{
        // [VERWIJDERD] Parallax transformatie in header
        transform: 'none'
      }} data-id="element-461">
          <div className="flex items-center justify-center gap-2 md:gap-3 mb-2" data-id="element-462">
            <motion.div animate={{
            rotate: 360
          }} // Alleen de rotatie behouden
          transition={{
            duration: 3,
            repeat: Infinity,
            ease: 'linear'
          }} className="relative" data-id="element-463">
              <Sparkles className={`w-5 h-5 md:w-6 md:h-6 ${isNeonTheme ? 'text-cyan-400' : 'text-fuchsia-400'}`} data-id="element-464" />
              {/* [VERWIJDERD] Pulsing overlay, alleen de statische Sparkles */}
            </motion.div>
            <h1 className={`text-2xl md:text-4xl font-bold ${isHighContrast ? 'text-white' : ''}`} style={{
            background: isHighContrast ? '#ffffff' : isNeonTheme ? 'linear-gradient(135deg, #00ffff, #ff00ff)' : 'linear-gradient(135deg, #ec4899, #8b5cf6)',
            WebkitBackgroundClip: isHighContrast ? 'unset' : 'text',
            WebkitTextFillColor: isHighContrast ? '#ffffff' : 'transparent',
            backgroundClip: isHighContrast ? 'unset' : 'text',
            textShadow: isNeonTheme ? '0 0 30px rgba(0, 255, 255, 0.5)' : undefined
          }} data-id="element-465">
              Portfolio
            </h1>
          </div>
          <p className={`${isLightTheme ? 'text-gray-600' : isHighContrast ? 'text-gray-300' : isNeonTheme ? 'text-cyan-300' : 'text-white/60'} text-xs md:text-base max-w-2xl mx-auto px-4`} data-id="element-466">
            Verken mijn werk als een levend holografisch systeem
          </p>
        </motion.div>
        {/* Filter Pills - Holographic Style */}
        <motion.div initial={{
        y: 20,
        opacity: 0
      }} animate={{
        y: 0,
        opacity: 1
      }} transition={{
        delay: 0.1
      }} className="overflow-x-auto -mx-4 px-4 md:mx-0 md:px-0 pb-2" data-id="element-467">
          <div className="flex items-center gap-2 min-w-max md:justify-center" data-id="element-468">
            {categories.map((cat, index) => {
            const Icon = cat.icon;
            const isActive = activeFilter === cat.id;
            return <motion.button key={cat.id} onClick={() => setActiveFilter(cat.id)} whileHover={{
              scale: 1.05
            }} whileTap={{
              scale: 0.95
            }} className={`relative px-4 md:px-6 py-2 md:py-3 rounded-xl text-xs md:text-sm font-medium transition-all whitespace-nowrap overflow-hidden ${isActive ? 'text-white' : ''}`} data-id="element-469">
                  {/* Glassmorphic background */}
                  <div className={`absolute inset-0 backdrop-blur-xl ${isActive ? isHighContrast ? 'bg-white' : isNeonTheme ? 'bg-gradient-to-r from-cyan-500/20 to-fuchsia-500/20 border-2 border-cyan-500' : 'bg-gradient-to-r from-fuchsia-500/20 to-violet-500/20' : isLightTheme ? 'bg-white/40 border border-gray-200' : isHighContrast ? 'bg-gray-900 border-2 border-white' : isNeonTheme ? 'bg-fuchsia-900/10 border border-cyan-500/30' : 'bg-white/5 border border-white/10'}`} data-id="element-470" />
                  {/* Animated gradient overlay for active */}
                  {isActive && !isHighContrast && <motion.div layoutId="activeFilter" className={`absolute inset-0 ${isNeonTheme ? 'bg-gradient-to-r from-cyan-500 to-fuchsia-500' : 'bg-gradient-to-r from-fuchsia-500 to-violet-500'}`} transition={{
                type: 'spring',
                bounce: 0.2,
                duration: 0.6
              }} data-id="element-471" />}
                  {/* Glow effect - Micro-animatie verwijderd */}
                  {isActive && !isHighContrast && <motion.div animate={{
                opacity: [0.5, 0, 0.5]
              }} transition={{
                duration: 2,
                repeat: Infinity
              }} className={`absolute inset-0 rounded-xl ${isNeonTheme ? 'bg-gradient-to-r from-cyan-500 to-fuchsia-500' : 'bg-gradient-to-r from-fuchsia-500 to-violet-500'} blur-xl`} data-id="element-472" />}
                  <div className="relative z-10 flex items-center gap-2" data-id="element-473">
                    <Icon className="w-3 h-3 md:w-4 md:h-4" data-id="element-474" />
                    <span className={isActive ? 'text-white' : isLightTheme ? 'text-gray-700' : isHighContrast ? 'text-white' : isNeonTheme ? 'text-cyan-300' : 'text-white/70'} data-id="element-475">
                      {cat.label}
                    </span>
                  </div>
                </motion.button>;
          })}
          </div>
        </motion.div>
      </div>
      {/* Project Grid - Holographic Portal Cards */}
      <div className="relative z-10 flex-1 overflow-y-auto px-4 md:px-8 pb-6 md:pb-8" data-id="element-476">
        <motion.div layout className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-8 max-w-7xl mx-auto" data-id="element-477">
          {filteredProjects.map((project, index) => <ProjectCard key={project.id} project={project} index={index} onClick={() => onProjectClick(project)}
        // [VERWIJDERD] mousePosition en isMobile props zijn verwijderd
        data-id="element-478" />)}
        </motion.div>
      </div>
      {/* Ambient Particles */}
      {!isHighContrast && <div className="absolute inset-0 pointer-events-none z-0" data-id="element-479">
          {[...Array(isMobile ? 10 : 20)].map((_, i) => <motion.div key={i} className={`absolute w-1 h-1 rounded-full ${isNeonTheme ? 'bg-cyan-400' : 'bg-fuchsia-400'}`} style={{
        left: `${Math.random() * 100}%`,
        top: `${Math.random() * 100}%`
      }} animate={{
        y: [0, -50, 0],
        opacity: [0.2, 0.8, 0.2],
        scale: [0, 1.5, 0]
      }} transition={{
        duration: 4 + Math.random() * 3,
        repeat: Infinity,
        delay: Math.random() * 2
      }} data-id="element-480" />)}
        </div>}
    </motion.div>;
}